import {addDays, addHours, differenceInCalendarDays, min, parseISO, startOfDay} from "date-fns"

import {TReachMeta} from "../../queries/reachEngine"
import {AResultsOverTimeListData} from "../../services/types.generated"
import {TReachPeriod} from "./period"

export type TResultsBucket = AResultsOverTimeListData["buckets"][number]

// A weekly bucket plotted at its week's end, as a timestamp on the time axis.
export type TResultsPoint = TResultsBucket & {date: number}

// Each week is plotted where it ends: its Sunday, or today for the current week. The backend's weeks start
// on Monday and its last week runs to today, so the last point is today.
export const toTimeline = (buckets: readonly TResultsBucket[], today: Date): TResultsPoint[] => {
  const day = startOfDay(today)
  return buckets.map((bucket, index) => ({
    ...bucket,
    date: (index === buckets.length - 1 ? day : min([addDays(parseISO(bucket.week_starting), 6), day])).getTime(),
  }))
}

// The run rate needs the last two weeks and the point before them.
const MIN_PROJECTED_BUCKETS = 3
// Headroom above the highest projected value, for its end-value pill.
const Y_HEADROOM = 1.08
// The pace we commit to: SQLs per month as a share of the prospects in flows of each type.
const TARGET_SQL_SHARE_PER_MONTH = {new_biz: 0.01, upsell: 0.05} as const
const DAYS_PER_MONTH = 30

export type TProjectedPoint = {date: number; sql: number; mql: number}

export type TResultsProjection = {
  sqlPerDay: number
  mqlPerDay: number
  // How many days after today the projection ends.
  horizonDays: number
  // What stopping all outreach today would cost by the end of the horizon.
  lostSql: number
  lostMql: number
  // Halfway through the horizon, and at its end.
  points: readonly [TProjectedPoint, TProjectedPoint]
  yMax: number
}

// The days the projection's horizon is based on: 30 for the last 30 days, and from the first week to today
// for the lifetime. 7 days is too short to project.
export const windowDaysFor = (period: TReachPeriod, timeline: readonly TResultsPoint[]) => {
  const first = timeline[0]
  const last = timeline[timeline.length - 1]
  if (period === "7d" || !first || !last) {
    return null
  }
  return period === "30d" ? 30 : differenceInCalendarDays(last.date, parseISO(first.week_starting)) + 1
}

// Projects the cumulative results from today at the run rate of the last two weeks, per elapsed day, so
// a partial current week doesn't drag the rate down. Needs at least 3 weeks. Neither metric is projected
// below the target pace for the prospects in flows: SQL at the target share, MQL at that times today's
// MQL to SQL ratio (at least 1, since every SQL was an MQL). Without SQL there is no ratio, so no MQL floor.
export const projectResults = (
  timeline: readonly TResultsPoint[],
  windowDays: number | null,
  prospects: TReachMeta["flow_types"]["leads"]
): TResultsProjection | null => {
  const n = timeline.length
  if (windowDays === null || n < MIN_PROJECTED_BUCKETS) {
    return null
  }
  const today = timeline[n - 1]
  const base = timeline[n - 3]
  const elapsedDays = Math.max(differenceInCalendarDays(today.date, base.date), 1)
  const sqlFloor =
    (TARGET_SQL_SHARE_PER_MONTH.new_biz * prospects.new_biz + TARGET_SQL_SHARE_PER_MONTH.upsell * prospects.upsell) /
    DAYS_PER_MONTH
  const mqlFloor = today.cumulative_sql > 0 ? sqlFloor * Math.max(today.cumulative_mql / today.cumulative_sql, 1) : 0
  const sqlPerDay = Math.max((today.cumulative_sql - base.cumulative_sql) / elapsedDays, sqlFloor)
  const mqlPerDay = Math.max((today.cumulative_mql - base.cumulative_mql) / elapsedDays, mqlFloor)
  const horizonDays = Math.round((2 * windowDays) / 3)

  const after = (days: number): TProjectedPoint => ({
    date: addHours(addDays(today.date, Math.floor(days)), (days % 1) * 24).getTime(),
    sql: today.cumulative_sql + sqlPerDay * days,
    mql: today.cumulative_mql + mqlPerDay * days,
  })
  const end = after(horizonDays)

  return {
    sqlPerDay,
    mqlPerDay,
    horizonDays,
    lostSql: Math.round(sqlPerDay * horizonDays),
    lostMql: Math.round(mqlPerDay * horizonDays),
    points: [after(horizonDays / 2), end],
    yMax: Math.max(end.mql, end.sql) * Y_HEADROOM,
  }
}

const AXIS_LABEL_COUNT = 4

// As in the design: at most four date labels, evenly spaced, the last one on the final week.
export const axisWeeks = <T>(points: readonly T[]): T[] => {
  const n = points.length
  if (n <= AXIS_LABEL_COUNT) {
    return [...points]
  }
  return Array.from({length: AXIS_LABEL_COUNT}, (_, i) => points[Math.round(((i + 1) * n) / AXIS_LABEL_COUNT) - 1])
}

// The least space between two date labels on the axis.
const AXIS_LABEL_GAP = 8

// How the time axis is drawn: the plot's width in pixels and each date label's estimated width.
export type TAxisLayout = {plotWidth: number; labelWidth: (date: number) => number}

// The actual weeks' labelled dates. A current week only a few days old can end so close after the week
// before it that their labels would overlap at the chart's width; that week's label is then left out.
const actualAxisDates = (dates: readonly number[], domain: readonly [number, number], layout?: TAxisLayout) => {
  const [start, end] = domain
  if (!layout || layout.plotWidth <= 0 || dates.length < 2 || end <= start) {
    return [...dates]
  }
  const today = dates[dates.length - 1]
  const previous = dates[dates.length - 2]
  const distance = ((today - previous) / (end - start)) * layout.plotWidth
  const needed = (layout.labelWidth(previous) + layout.labelWidth(today)) / 2 + AXIS_LABEL_GAP
  return distance < needed ? [...dates.slice(0, -2), today] : [...dates]
}

// The dates labelled on the time axis: up to four actual weeks, then the projected points. Without a
// layout, every evenly spaced week is labelled.
export const axisTicks = (
  timeline: readonly TResultsPoint[],
  projection: TResultsProjection | null,
  layout?: TAxisLayout
) => {
  const projected = projection?.points.map(point => point.date) ?? []
  const first = timeline[0]?.date ?? 0
  const last = projected.at(-1) ?? timeline.at(-1)?.date ?? 0
  return [
    ...actualAxisDates(
      axisWeeks(timeline).map(point => point.date),
      [first, last],
      layout
    ),
    ...projected,
  ]
}

const VALUE_TICK_INTERVALS = 4

// Whole-number value ticks for the domain [0, yMax], at a round step (1, 2 or 5 times a power of ten), so the
// axis doesn't end on an odd label at yMax itself.
export const valueTicks = (yMax: number) => {
  const raw = Math.max(yMax / VALUE_TICK_INTERVALS, 1)
  const power = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 5, 10].map(factor => factor * power).find(candidate => candidate >= raw) ?? 10 * power
  return Array.from({length: Math.floor(yMax / step) + 1}, (_, i) => i * step)
}
